/**
 * The diplomatic turn: the clock, and the council's other half.
 *
 * `turn.ts` calls `diplomaticTurn` once, in the Council phase, immediately before
 * the military council sits. The order is deliberate — a nation should know who
 * it is at war with *before* it decides where to march, not after — and it is the
 * only line this system asked of a file somebody else owns.
 *
 * Everything stochastic in diplomacy is in here, and all of it comes off
 * `w.rngCampaign`. Note what the rolls actually decide: whether a crown *asks*
 * for something this turn. Whether the answer is yes is a deterministic threshold
 * in `diploacts.ts`, so a player can learn the realm rather than merely
 * re-rolling it.
 *
 * ── The council's policy, in full ─────────────────────────────────────────
 *
 *   1. **Sue or dictate.** Every war a crown is a principal in is looked at. If
 *      it is far enough ahead it names its price; if it is far enough behind it
 *      offers one. Against another AI that resolves on the spot; against the
 *      player it becomes an offer, because the player has to be allowed to say no.
 *   2. **Declare**, on a bordering crown it can beat, for a reason it has.
 *   3. **Ally**, with somebody it likes who has room.
 *   4. **Marry**, with somebody it does not actively hate.
 *   5. **Swallow** a subject it has held long enough.
 *
 * Every step is bounded by the *border* graph rather than by the nation count, so
 * the per-turn cost is linear in the map's edges. The one exception is the
 * opinion drift, which is `n²` and is argued for where it is written.
 */

import { nextFloat } from '../../core/rng.ts';
import { nationDef } from '../../data/nations.ts';
import { provinceDef } from '../../data/provinces.ts';
import type { World } from '../world.ts';
import {
  addOffer,
  addOpinion,
  alliesOf,
  coalitionStrength,
  driftOpinions,
  dropAllTreaties,
  expireTruces,
  freeSubject,
  hasMarriage,
  hasOfferFrom,
  liegeOf,
  marriagesOf,
  opinion,
  provinceWorth,
  provincesHeld,
  neighbourNations,
  subjectsOf,
  truceBetween,
} from './diplomacy.ts';
import {
  ALLIANCE_OPINION,
  MARRIAGE_OPINION,
  annexReason,
  annexSubject,
  expireOffers,
  passageReason,
  proposeAlliance,
  proposeMarriage,
  requestPassage,
  revokePassage,
} from './diploacts.ts';
import {
  canReachOnFoot,
  isTrespassing,
  mayEnter,
  passageGrantedBy,
  passageWouldOpenARoad,
} from './diplopass.ts';
import {
  CasusBelli,
  COST_VASSALISE,
  MAX_ALLIES,
  OFFER_TURNS,
  OfferKind,
  OP_TRESPASS,
  PASSAGE_REVOKE_OPINION,
  PeaceTerm,
  SubjectKind,
  UNION_INHERIT_CHANCE,
  UNION_MARRIAGE_TURNS,
  UNION_TRIBUTE,
  VASSAL_TRIBUTE,
  type War,
} from './diplotypes.ts';
import { forceRealmEvent } from './chronicleturn.ts';
import {
  availableTerms,
  canDeclareWar,
  casusBelliAgainst,
  declareWar,
  makePeace,
  reapWars,
  scoreFor,
  sideOf,
  termCost,
  warWeariness,
  warsOf,
  type PeaceOption,
} from './war.ts';
import { logLine, nationName, provinceName } from './state.ts';
import { yieldScale } from './holdings.ts';
import { ArmyStance, type CampaignState } from './types.ts';

// ── The numbers the council is tuned around ────────────────────────────────

/** War score at which a crown starts naming a price rather than fighting on. */
const DICTATE_SCORE = 25;
/** And at which the losing side starts offering one. */
const SUE_SCORE = 25;
/** Turns before a stalemate is worth ending for nothing. */
const STALEMATE_TURNS = 30;
/** Turns between successive peace offers to the same player, so it is not nagging. */
const OFFER_COOLDOWN = 4;

/**
 * The margin a crown wants before it declares.
 *
 * Lower than the margin `ai.ts` demands before it will *march*, and it has to be:
 * that one is about whether a host wins one fight next door, this one is about
 * whether a kingdom wins a war. A crown that insisted on two to one over the
 * whole realm before declaring anything would never declare anything, and the
 * board would go quiet — which is the failure mode `campaign-balance.test.ts`
 * exists to catch and which the first draft of this walked straight into.
 */
const DECLARE_MARGIN_BASE = 0.82;
const DECLARE_MARGIN_PER_CAUTION = 0.6;
/** How much being disliked lowers the bar against a particular crown. */
const DECLARE_SPITE = 0.22;

/** Per-turn chances that a crown even considers each of these. */
const ALLIANCE_CHANCE = 0.16;
const MARRIAGE_CHANCE = 0.14;

// ── The whole turn ─────────────────────────────────────────────────────────

export function diplomaticTurn(w: World): void {
  const c = w.campaign;
  if (!c || c.over) return;

  reapDeadNations(c);
  reapWars(c);
  expireTruces(c);
  expireOffers(c);
  warWeariness(c);
  payTribute(c);
  rollInheritance(w, c);
  // Before the drift, so a crown that spent the month with somebody's army in
  // its fields has that in the number the drift is pulling toward.
  passageTurn(c);
  driftOpinions(c);
  council(w, c);
}

// ── Hosts standing where they may not ──────────────────────────────────────

/**
 * The month's reckoning for every host on ground it has no right to be on.
 *
 * On the council rather than in the tick, per the realm's rule, and it is the
 * only place `Army.trespassT` is written. Three months of grace, then the
 * countryside stops feeding them — `updateSupply` reads `isTrespassing`, so the
 * hunger arithmetic that ends sieges ends this too — and the crown whose ground
 * it is thinks worse of theirs every month they stay.
 *
 * This fires constantly, and that is the point. **Every peace strands both
 * sides' armies on each other's territory**, and before this they stayed there,
 * provisioned to full by the country they had just invaded, until the campaign
 * ended. So does a revoked grant, and so does the end of a war two allies were
 * fighting together, which is the one that will surprise people.
 *
 * The counter is reset rather than merely not incremented, so a host that steps
 * back onto legal ground — or whose landlord relents, or who is handed a new war
 * against the crown it is standing on — starts clean. Nobody carries an old
 * overstay into a new one.
 */
export function passageTurn(c: CampaignState): void {
  for (const a of c.armies) {
    if (a.aboard >= 0 || a.moveTo >= 0) {
      // On the water or on the road: not standing on anybody's ground. The clock
      // holds rather than resets — a host that steps out of a province and back
      // into it has not served its notice by walking in a circle.
      continue;
    }
    if (mayEnter(c, a.owner, a.at)) {
      a.trespassT = 0;
      continue;
    }
    const landlord = c.provinces[a.at].owner;
    const wasTrespassing = isTrespassing(a);
    a.trespassT++;

    /**
     * A siege cannot outlive the right to be there.
     *
     * `doSieges` asks only whether the stance is `Besieging`; it does not ask
     * whether there is a war on. Standing this host down is what stops a host
     * left behind by a peace grinding a garrison to nothing and taking a
     * province off a crown it is not fighting. `endWar` clears the stance for
     * the hosts it can see; this catches the rest — a revoked grant, an
     * alliance's shared war ending, a subject released.
     */
    if (a.stance !== ArmyStance.Field) {
      a.stance = ArmyStance.Field;
      a.siegeTurns = 0;
      if (c.provinces[a.at].siegeBy === a.owner) {
        c.provinces[a.at].siegeBy = -1;
        c.provinces[a.at].siegeT = 0;
      }
    }

    if (landlord >= 0) addOpinion(c, landlord, a.owner, OP_TRESPASS);

    /**
     * Two lines a campaign, and only when the player is one of the two.
     *
     * The chronicle is forty-eight lines and it is the player's, not a
     * transcript of the realm — the admiralty's note on this is the standing
     * argument. So: the month the grace runs out, and only then, and only if
     * the host or the ground is theirs. Everything else reaches them through
     * `passageView`, which is a panel and can afford to list.
     */
    if (!wasTrespassing && isTrespassing(a) && (a.owner === c.playerNation || landlord === c.playerNation)) {
      logLine(
        c,
        a.owner === c.playerNation
          ? `${a.name} has outstayed its welcome in ${provinceName(a.at)}. ` +
              `${nationName(c, landlord)} will not feed them.`
          : `${nationName(c, a.owner)}'s host in ${provinceName(a.at)} has outstayed ` +
              `its welcome. The countryside is closed to them.`,
      );
    }
  }
}

/**
 * A crown that has been eliminated cannot hold treaties, cannot be somebody's
 * vassal and cannot have vassals of its own.
 *
 * `checkElimination` lives in `state.ts` and knows nothing about diplomacy on
 * purpose — the two files would otherwise import each other — so the tidying
 * happens here, once a turn, which is soon enough: a dead crown holds no ground
 * and fields no hosts, so nothing can act on a stale treaty in the meantime.
 */
function reapDeadNations(c: CampaignState): void {
  for (let n = 0; n < c.nations.length; n++) {
    const nation = c.nations[n];
    if (nation.alive) continue;
    if (nation.liege >= 0 || nation.subject !== SubjectKind.None) freeSubject(c, n);
    if (c.diplo.treaties.some((t) => t.a === n || t.b === n)) dropAllTreaties(c, n);
    for (const s of subjectsOf(c, n)) {
      freeSubject(c, s);
      logLine(c, `${nationName(c, s)} answers to nobody now. There is nobody left to answer to.`);
    }
  }
  c.diplo.offers = c.diplo.offers.filter(
    (o) => c.nations[o.from]?.alive && c.nations[o.to]?.alive,
  );
}

// ── What a subject pays ────────────────────────────────────────────────────

/**
 * A vassal pays, and a junior partner barely does.
 *
 * The gold is computed here rather than taken from `projectedIncome` in
 * `turn.ts`, and that is a deliberate duplication of six lines: the turn file
 * belongs to somebody who is in the middle of removing turns from this game
 * entirely, and a tribute that stops working when that lands would be a silent
 * failure rather than a compile error. What is duplicated is the *province*
 * yield only — a subject's army wages are its own problem, which is also the
 * right answer, because a liege taxes what its vassal collects rather than what
 * it has left.
 */
function payTribute(c: CampaignState): void {
  for (let n = 0; n < c.nations.length; n++) {
    const nation = c.nations[n];
    if (!nation.alive || nation.liege < 0) continue;
    const liege = c.nations[nation.liege];
    if (!liege || !liege.alive) continue;
    const share = nation.subject === SubjectKind.Union ? UNION_TRIBUTE : VASSAL_TRIBUTE;
    const due = Math.min(nation.treasury.gold, crownYield(c, n) * share);
    if (due <= 0) continue;
    nation.treasury.gold -= due;
    liege.treasury.gold += due;
  }
}

/** What a crown's provinces are worth to it this turn, before anything is spent. */
function crownYield(c: CampaignState, n: number): number {
  let gold = 0;
  for (let p = 0; p < c.provinces.length; p++) {
    const prov = c.provinces[p];
    if (prov.owner !== n) continue;
    // The tier is in `yieldScale`, so a vassal that has been *built up* pays a
    // tribute that reflects it. Tribute read off a formula that had drifted from
    // the one in `doIncome` would quietly have charged a great city hamlet rates.
    gold += provinceDef(p).wealth * yieldScale(c, p);
  }
  return gold;
}

// ── A crown falling in ─────────────────────────────────────────────────────

/**
 * An old marriage, and the day a crown simply arrives.
 *
 * This is the one thing in the system that happens *to* the player rather than
 * being asked of them, and it is why marriages are worth signing with crowns you
 * have no particular use for. The guards are all about keeping it from being a
 * mechanic: it needs a marriage that has stood thirty-four turns, two crowns not
 * at war, neither already sworn to anybody, and a clear difference in size — and
 * then it still only fires at well under one per cent a turn.
 *
 * **The player is never the junior.** A player's crown passing to somebody else
 * would end the run through a dice roll they had no hand in, which is precisely
 * the thing this project's fairness rule exists to forbid. They can inherit; they
 * cannot be inherited.
 */
function rollInheritance(w: World, c: CampaignState): void {
  for (const m of c.diplo.treaties.slice()) {
    if (!hasMarriage(c, m.a, m.b)) continue;
    if (c.turn - m.since < UNION_MARRIAGE_TURNS) continue;
    if (!c.nations[m.a].alive || !c.nations[m.b].alive) continue;
    if (liegeOf(c, m.a) >= 0 || liegeOf(c, m.b) >= 0) continue;
    if (c.nations[m.a].atWar[m.b] === 1) continue;

    const heldA = provincesHeld(c, m.a);
    const heldB = provincesHeld(c, m.b);
    if (heldA === heldB) continue;
    let senior = heldA > heldB ? m.a : m.b;
    let junior = senior === m.a ? m.b : m.a;
    if (junior === c.playerNation) {
      // Never downward onto the player. If the player is the larger of the two
      // this is the ordinary case; if not, the succession simply does not fall.
      if (senior !== c.playerNation) continue;
      const swap = senior;
      senior = junior;
      junior = swap;
    }
    if (nextFloat(w.rngCampaign) >= UNION_INHERIT_CHANCE) continue;

    c.nations[junior].liege = senior;
    c.nations[junior].subject = SubjectKind.Union;
    c.nations[junior].subjectSince = c.turn;
    logLine(
      c,
      `The line of ${nationName(c, junior)} fails, and the marriage holds: ` +
        `${nationName(c, senior)} wears both crowns.`,
    );
  }
}

// ── The council ────────────────────────────────────────────────────────────

function council(w: World, c: CampaignState): void {
  const neigh = neighbourNations(c);
  for (let n = 0; n < c.nations.length; n++) {
    if (!c.nations[n].alive) continue;
    // The player's own diplomacy is the player's. The AI still acts *toward*
    // them — peace offers, calls to arms, declarations — from the other side.
    considerPeace(c, n);
    if (n === c.playerNation) continue;
    if (liegeOf(c, n) >= 0) continue; // a sworn crown does not make its own policy
    considerWar(w, c, n, neigh);
    // Passage before the friendlier arrangements, and after the war: a crown
    // asks for a road because it has a war it cannot reach, so the war has to
    // have been declared before the question can be asked.
    considerPassage(c, n, neigh);
    considerRevoke(c, n);
    considerAlliance(w, c, n, neigh);
    considerMarriage(w, c, n, neigh);
    considerAnnex(c, n);
  }
}

// ── 2b. Ask for a road, and take one back ──────────────────────────────────

/**
 * Send a herald to the country that is in the way.
 *
 * The gate is the whole of the policy and it is one sentence: **ask only when
 * there is a war you cannot reach, and only of the crown whose ground would
 * open it.** No roll — a crown that needs a road needs it every month until it
 * has one, and a random herald would make the answer to "why did nobody ask"
 * be "the dice", which is exactly what this system's rolls are kept away from.
 *
 * The alternative shape — ask everybody you like for passage as a matter of
 * course — was rejected. Passage with no war behind it is a treaty nobody can
 * read a motive into, and it would have every border on the board open by turn
 * forty, which is the same as not having the feature.
 *
 * Cost is bounded by the border graph, like everything else the council does:
 * one breadth-first search per crown you border, per war, and only for crowns
 * that are at war at all. In a realm at peace it costs the length of `warsOf`,
 * which is zero.
 */
function considerPassage(c: CampaignState, n: number, neigh: Set<number>[]): void {
  const wars = warsOf(c, n);
  if (wars.length === 0) return;

  for (const war of wars) {
    const foe = sideOf(war, n) === 1 ? war.defender : war.attacker;
    if (!c.nations[foe]?.alive) continue;
    // Already able to walk at them: nothing to ask anybody for.
    if (canReachOnFoot(c, n, foe)) continue;

    for (const through of [...neigh[n]].sort((a, b) => a - b)) {
      if (through === foe) continue;
      if (passageReason(c, through, n) !== '') continue;
      if (hasOfferFrom(c, OfferKind.Passage, n, through)) continue;
      if (!passageWouldOpenARoad(c, n, through, foe)) continue;
      requestPassage(c, n, through);
      // One herald a month. A crown that sent three at once and was refused by
      // all three has spent its whole standing in one turn for one road.
      return;
    }
  }
}

/**
 * Shut a road that should not still be open.
 *
 * Two reasons, and both of them are things the grantee did. **They have become
 * an enemy of an ally** — nobody keeps their border open to an army marching at
 * their friend, and this is the clause that makes granting passage a decision
 * with a shelf life rather than a permanent gift. **Or they have simply become
 * hated**, at a floor well below the bar for granting in the first place, so
 * there is a wide band in which a grant survives a cooling relationship. A grant
 * that lapsed the moment opinion dipped under what it took to get one would
 * flicker on and off every few months and mean nothing.
 *
 * Not rolled, like the asking, and for the same reason: the player has to be
 * able to see this coming.
 */
function considerRevoke(c: CampaignState, n: number): void {
  for (const grantee of passageGrantedBy(c, n)) {
    const threatensAFriend = alliesOf(c, n).some((ally) => c.nations[grantee]?.atWar[ally] === 1);
    if (threatensAFriend || opinion(c, n, grantee) < PASSAGE_REVOKE_OPINION) {
      revokePassage(c, n, grantee);
      return;
    }
  }
}

// ── 1. Sue or dictate ──────────────────────────────────────────────────────

function considerPeace(c: CampaignState, n: number): void {
  for (const war of warsOf(c, n).slice()) {
    if (war.attacker !== n && war.defender !== n) continue;
    const foe = war.attacker === n ? war.defender : war.attacker;
    const mine = scoreFor(war, n);
    const stale = c.turn - war.startTurn >= STALEMATE_TURNS;

    if (n === c.playerNation) {
      // Only the AI initiates; the player has commands for this.
      continue;
    }

    if (mine >= DICTATE_SCORE || (stale && mine > 0)) {
      dictate(c, war, n, foe, mine);
      continue;
    }
    if (mine <= -SUE_SCORE || (stale && mine <= 0)) {
      sue(c, war, n, foe, -mine);
    }
  }
}

/**
 * Name a price. The best thing the score will buy, and nothing beyond it.
 *
 * With one exception, and it is the reason vassals exist at all. Cashing in at
 * the first opportunity means every war ends the moment somebody is twenty-five
 * points ahead, which is enough for a province and never enough for a crown — so
 * a system with subjugation in it would go a whole campaign without producing a
 * single subject. A crown that is *comfortably* winning and has not been at it
 * long therefore holds out for the bigger settlement instead of signing for a
 * field. Patience is bounded by both clauses on purpose: it lapses once the war
 * is twenty turns old, and it never applies to a crown that is merely ahead.
 */
function dictate(c: CampaignState, war: War, n: number, foe: number, score: number): void {
  if (isPatient(c, war, n, foe, score)) return;
  const best = bestTerm(c, war, n, score);
  if (!best) return;
  if (foe !== c.playerNation) {
    makePeace(c, war, n, best.term, best.province);
    return;
  }
  if (c.turn - war.lastOffer < OFFER_COOLDOWN) return;
  war.lastOffer = c.turn;
  addOffer(c, {
    kind: OfferKind.Peace,
    from: n,
    to: foe,
    war: war.id,
    term: best.term,
    province: best.province,
    gold: best.gold,
    fromConcedes: false,
    expires: c.turn + OFFER_TURNS,
  });
  logLine(c, `${nationName(c, n)} sends terms: ${best.label}.`);
}

/**
 * Whether the winner would rather keep fighting than sign for what it has won.
 *
 * Used from both ends of the negotiation, and it has to be. Asked only in
 * `dictate`, the losing crown could still pre-empt the whole thing by *suing* —
 * offering a province at a deficit of twenty-five, which an AI winner then took
 * on the spot — and a campaign would end without a single crown ever being
 * subjugated, because no war was ever allowed to run long enough to be worth a
 * crown. Which is exactly what eight measured realms did.
 */
function isPatient(
  c: CampaignState,
  war: War,
  winner: number,
  loser: number,
  score: number,
): boolean {
  if (winner === c.playerNation) return false;
  if (score >= COST_VASSALISE) return false;
  if (c.turn - war.startTurn >= 20) return false;
  return coalitionStrength(c, winner) > coalitionStrength(c, loser) * 1.5;
}

/**
 * Offer one. A beaten crown concedes rather less than it has actually lost, which
 * is both how negotiation works and how a player gets to feel they squeezed.
 */
function sue(c: CampaignState, war: War, n: number, foe: number, deficit: number): void {
  // A crown holding out for a bigger settlement is not interested in yours.
  if (isPatient(c, war, foe, n, deficit)) return;
  const offering = Math.round(deficit * 0.7);
  const best = bestTerm(c, war, foe, offering);
  if (!best) return;
  if (foe !== c.playerNation) {
    // Two AI crowns: the winner simply takes what is offered.
    makePeace(c, war, foe, best.term, best.province);
    return;
  }
  if (c.turn - war.lastOffer < OFFER_COOLDOWN) return;
  war.lastOffer = c.turn;
  addOffer(c, {
    kind: OfferKind.Peace,
    from: n,
    to: foe,
    war: war.id,
    term: best.term,
    province: best.province,
    gold: best.gold,
    fromConcedes: true,
    expires: c.turn + OFFER_TURNS,
  });
  logLine(c, `${nationName(c, n)} sues for peace: ${best.label}.`);
}

/**
 * The best demand a given number of war-score points will buy.
 *
 * Reads the same `availableTerms` list the peace panel shows the player, which is
 * the point: the AI cannot want something the player cannot see it wanting. Ties
 * break toward taking ground, because ground is the thing this game is about and
 * an AI that reliably preferred gold would make every war feel like a tax.
 */
function bestTerm(
  c: CampaignState,
  war: War,
  taker: number,
  budget: number,
): PeaceOption | undefined {
  const options = availableTerms(c, war, taker).filter((o) => o.cost <= budget);
  if (options.length === 0) return undefined;
  const rank = (o: PeaceOption): number => {
    if (o.term === PeaceTerm.Union) return 5;
    if (o.term === PeaceTerm.Vassalise) return 4;
    if (o.term === PeaceTerm.Cede) return 3;
    if (o.term === PeaceTerm.Tribute) return 2;
    if (o.term === PeaceTerm.Concessions) return 1;
    return 0;
  };
  return options.reduce((best, o) =>
    rank(o) > rank(best) || (rank(o) === rank(best) && o.cost > best.cost) ? o : best,
  );
}

// ── 2. Declare ─────────────────────────────────────────────────────────────

function considerWar(w: World, c: CampaignState, n: number, neigh: Set<number>[]): void {
  // One war at a time. Not a realism claim — a claim about legibility: a crown
  // fighting three wars produces a board nobody, player or council, can read.
  if (warsOf(c, n).length > 0) return;
  const def = nationDef(c.nations[n].def);
  if (nextFloat(w.rngCampaign) > 0.05 + def.aggression * 0.5) return;

  const mine = coalitionStrength(c, n);
  let target = -1;
  let bestValue = 0;
  let bestCb: number = CasusBelli.None;

  for (const foe of [...neigh[n]].sort((a, b) => a - b)) {
    if (!canDeclareWar(c, n, foe)) continue;
    const theirs = Math.max(1, coalitionStrength(c, foe));
    const dislike = Math.max(0, -opinion(c, n, foe)) / 100;
    const need =
      (DECLARE_MARGIN_BASE + def.caution * DECLARE_MARGIN_PER_CAUTION) * (1 - dislike * DECLARE_SPITE);
    if (mine < theirs * need) continue;

    const reasons = casusBelliAgainst(c, n, foe);
    const cb = reasons.find((r) => r !== CasusBelli.None) ?? CasusBelli.None;
    // A crown with no pretext at all needs to be the kind of crown that does not
    // care. Everyone else waits until the board hands them a reason.
    if (cb === CasusBelli.None && def.aggression < 0.6) continue;

    // What is actually on the table: the fattest province of theirs we could
    // plausibly end up holding, weighted by how much we dislike them.
    let prize = 0;
    for (let p = 0; p < c.provinces.length; p++) {
      if (c.provinces[p].owner !== foe) continue;
      prize = Math.max(prize, provinceWorth(c, p));
    }
    const value = prize * (1 + dislike) * (mine / theirs);
    if (value > bestValue) {
      bestValue = value;
      target = foe;
      bestCb = cb;
    }
  }

  if (target < 0) return;
  const goal = richestOf(c, target);
  declareWar(c, n, target, bestCb, goal);
  /**
   * A declaration made *at a person* stops that person's world.
   *
   * It used to be a line in the chronicle, weighted the same as a harvest
   * report, on a panel nobody is looking at when it lands — and it is the most
   * consequential thing another crown can do to you. Opened by name rather than
   * left to the deck's own cadence, because the deck picks by weight and a card
   * about a war that started three turns ago is a strange kind of news.
   *
   * `forceRealmEvent` refuses if the card's `when` does not currently hold, so
   * a declaration that somehow did not take leaves no window behind it. Only a
   * human crown gets the question: an AI defender answers its own cards through
   * `answerAsCrown`, and would have this one put to it there.
   */
  if (c.humanNations.includes(target)) forceRealmEvent(c, 'war_declared', target);
}

function richestOf(c: CampaignState, nation: number): number {
  let best = -1;
  let bestWorth = -1;
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== nation) continue;
    const worth = provinceWorth(c, p);
    if (worth > bestWorth) {
      bestWorth = worth;
      best = p;
    }
  }
  return best;
}

// ── 3. Ally, 4. Marry ──────────────────────────────────────────────────────

/**
 * Candidates are neighbours, and neighbours of neighbours.
 *
 * Not every crown on the board: at thirty kingdoms that would be the one place
 * the council became quadratic, and it would also be wrong. A crown two borders
 * away is somebody you can plausibly have heard of and plausibly need — one on
 * the far side of the realm is a name on a list. Two hops is where the line goes.
 */
function diplomaticRange(c: CampaignState, n: number, neigh: Set<number>[]): number[] {
  const out = new Set<number>();
  for (const a of neigh[n]) {
    out.add(a);
    for (const b of neigh[a]) if (b !== n) out.add(b);
  }
  return [...out].filter((x) => c.nations[x]?.alive).sort((a, b) => a - b);
}

function considerAlliance(w: World, c: CampaignState, n: number, neigh: Set<number>[]): void {
  if (alliesOf(c, n).length >= MAX_ALLIES) return;
  if (nextFloat(w.rngCampaign) > ALLIANCE_CHANCE) return;

  let best = -1;
  let bestScore = 0;
  for (const other of diplomaticRange(c, n, neigh)) {
    if (other === c.playerNation && hasOfferFrom(c, OfferKind.Alliance, n, other)) continue;
    const op = opinion(c, n, other);
    if (op < ALLIANCE_OPINION) continue;
    if (alliesOf(c, other).length >= MAX_ALLIES) continue;
    if (c.nations[n].atWar[other] === 1) continue;
    if (liegeOf(c, other) >= 0) continue;
    // Somebody who dislikes what you dislike is worth more than somebody who
    // merely likes you. This is the whole of the "shared enemy" model.
    let shared = 0;
    for (const war of warsOf(c, other)) if (sideOf(war, n) !== 0) shared += 20;
    const score = op + shared;
    if (score > bestScore) {
      bestScore = score;
      best = other;
    }
  }
  if (best < 0) return;
  proposeAlliance(c, n, best);
}

function considerMarriage(w: World, c: CampaignState, n: number, neigh: Set<number>[]): void {
  if (nextFloat(w.rngCampaign) > MARRIAGE_CHANCE) return;
  let best = -1;
  let bestOp = MARRIAGE_OPINION;
  for (const other of diplomaticRange(c, n, neigh)) {
    if (hasMarriage(c, n, other)) continue;
    if (c.nations[n].atWar[other] === 1) continue;
    if (other === c.playerNation && hasOfferFrom(c, OfferKind.Marriage, n, other)) continue;
    const op = opinion(c, n, other);
    if (op < bestOp) continue;
    bestOp = op;
    best = other;
  }
  if (best < 0) return;
  proposeMarriage(c, n, best);
}

// ── 5. Swallow ─────────────────────────────────────────────────────────────

function considerAnnex(c: CampaignState, n: number): void {
  for (const s of subjectsOf(c, n)) {
    if (annexReason(c, n, s) !== '') continue;
    annexSubject(c, n, s);
    return;
  }
}

// ── What the player's own peace offer is worth ─────────────────────────────

/**
 * The player has put terms on the table. Does the other crown sign?
 *
 * Two directions, and they are not symmetrical. A player *demanding* something
 * is refused unless the war score genuinely covers it — the AI does not have to
 * be generous, it has to be consistent. A player *conceding* something is
 * accepted whenever it is worth at least what the AI thinks it is owed, less a
 * little, because a crown that is winning and offered most of what it wanted has
 * no reason to keep paying for an army.
 */
export function evaluatePlayerPeace(
  c: CampaignState,
  war: War,
  term: number,
  province: number,
): boolean {
  const player = c.playerNation;
  const side = sideOf(war, player);
  if (side === 0) return false;
  const foe = war.attacker === player ? war.defender : war.attacker;
  if (war.attacker !== player && war.defender !== player) return false;

  const mine = scoreFor(war, player);
  const playerCost = termCost(c, war, player, term, province);

  // A demand: the player is the taker.
  if (playerCost >= 0 && playerCost <= Math.max(0, mine)) {
    // The AI accepts what the score says it must, and accepts a white peace
    // whenever it is not itself winning.
    if (term === PeaceTerm.White) return -mine <= DICTATE_SCORE;
    return true;
  }

  // A concession: the player is paying, so the AI is the taker.
  const theirCost = termCost(c, war, foe, term, province);
  if (theirCost < 0) return false;
  const theirs = scoreFor(war, foe);
  if (theirCost > Math.max(0, theirs)) {
    // They are being offered more than they have won. Nobody refuses that.
    return true;
  }
  return theirCost >= Math.max(0, theirs) * 0.6;
}

/** Who signs, when the player puts a term on the table. */
export function peaceTaker(c: CampaignState, war: War, term: number, province: number): number {
  const player = c.playerNation;
  const foe = war.attacker === player ? war.defender : war.attacker;
  const cost = termCost(c, war, player, term, province);
  if (cost >= 0 && cost <= Math.max(0, scoreFor(war, player))) return player;
  return foe;
}

/** The player's own marriages, for the panel's "houses joined" line. */
export function playerMarriages(c: CampaignState): number[] {
  return marriagesOf(c, c.playerNation)
    .map((t) => (t.a === c.playerNation ? t.b : t.a))
    .sort((a, b) => a - b);
}

/** True when a truce is the reason a button is greyed out. For the panel. */
export function blockedByTruce(c: CampaignState, a: number, b: number): boolean {
  return truceBetween(c, a, b);
}
